import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { SelectorCuentaPago } from '../components/CuentaPago';
import { ModalConfirmarCuenta, AvisoCuentaProveedor } from '../components/CuentasProveedor';
import { Spinner, Toast, css, Modal, PageHeader, EstadoBadge, IconBtn,
         ESTADO_COLORS, opcionesEstado, ESTADO_OPCION_LABEL, Paginacion, ESTADO_LABEL , DestinoPagoFijo } from '../components/UI';
import { VisorAdjuntos } from '../components/VisorAdjuntos';

// ── Metadatos por tipo ────────────────────────────────────────────────────────
const TIPO_META = {
  // 'pago' es la solicitud de gastos en general; se nombra así en el PDF y en el dashboard
  pago:                 { label: 'Gastos en General', color: '#3d4148', bg: 'rgba(0,0,0,0.05)' },
  debito:               { label: 'Débito',             color: '#be123c', bg: 'rgba(225,29,72,0.10)' },
  contribuciones:       { label: 'Contribuciones',     color: '#6d28d9', bg: 'rgba(124,58,237,0.10)' },
  activos_fijos:        { label: 'Activos Fijos',      color: '#0f766e', bg: 'rgba(13,148,136,0.10)' },
  mercancias_nacionales:{ label: 'Mercancías Nac.',    color: '#b45309', bg: 'rgba(217,119,6,0.10)' },
  nomina:               { label: 'Nómina',             color: '#4338ca', bg: 'rgba(79,70,229,0.10)' },
  reembolso:            { label: 'Reembolso',          color: '#0369a1', bg: 'rgba(14,165,233,0.10)' },
  finiquito:            { label: 'Finiquito',          color: '#c4a1ff', bg: 'rgba(139,92,246,0.10)' },
  caja_chica:           { label: 'Caja Chica',         color: '#15803d', bg: 'rgba(5,150,105,0.10)'   },
  reembolso_gastos:     { label: 'Reembolso Gastos',   color: '#be185d', bg: 'rgba(190,24,93,0.10)'   },
  complemento_importacion: { label: 'Compl. Importación', color: '#0f766e', bg: 'rgba(15,118,110,0.10)' },
};

// Devuelve la clave de tipo (combinando _coleccion y tipo de solicitud)
const tipoDeItem = (s) => {
  if (s._coleccion === 'reembolso') return 'reembolso';
  if (s._coleccion === 'finiquito') return 'finiquito';
  return s.tipo || 'pago';
};

// Endpoint base según colección
const epBase = (s) =>
  s._coleccion === 'reembolso' ? '/reembolsos' :
  s._coleccion === 'finiquito' ? '/finiquitos' :
  '/solicitudes';

// Catálogo del que se elige el proveedor/cliente/beneficiario según la colección
const catalogoDe = (s) =>
  s?._coleccion === 'reembolso' ? { endpoint: '/clientes', label: 'Cliente' } :
  s?._coleccion === 'finiquito' ? { endpoint: '/catalogo-finiquitos', label: 'Beneficiario' } :
  { endpoint: '/proveedores', label: 'Proveedor' };

const BASE = process.env.REACT_APP_API_URL || '/api';
const ROL_COLORS = { admin: '#6d28d9', finanzas: '#1d4ed8', solicitante: '#047857', contador: '#0f766e', programacion: '#be185d' };

const fmt = (n) => (Number(n) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 });
const isoLocal = (d) => d ? new Date(d).toLocaleDateString('en-CA') : '';

// ¿Necesita tipo de cambio? Sólo lo que no está en pesos.
const esExtranjera = (s) => !!s && (s.moneda || 'MXN') !== 'MXN';
// De dónde salió la tasa con la que se convirtió a pesos, para poder marcar lo estimado
const TC_ORIGEN = {
  estimado:   { label: 'estimado',   color: 'var(--st-pendiente)', titulo: 'Tipo de cambio de referencia: aún no se programa el pago' },
  programado: { label: 'programado', color: 'var(--st-por-pagar)', titulo: 'Tipo de cambio capturado al programar el pago' },
  pagado:     { label: 'real',       color: 'var(--ok)', titulo: 'Tipo de cambio real con el que se ejecutó el pago' },
  sin_tipo_cambio: { label: 'sin T.C.', color: 'var(--danger)', titulo: 'No hay tipo de cambio de referencia para esta moneda: se está sumando 1 a 1' },
};

// Devuelve el endpoint del catálogo de motivos según el tipo de solicitud
const catalogoMotivos = (s) => {
  // Los finiquitos se capturan con el catálogo de nómina, no con el de gastos
  if (s._coleccion === 'finiquito') return '/motivos-nomina';
  if (s._coleccion === 'reembolso') return '/motivos-reembolso';
  if (s._coleccion !== 'solicitud') return '/motivos';
  const tipo = s.tipo || 'pago';
  if (tipo === 'debito') return '/motivos-debito';
  if (tipo === 'contribuciones') return '/motivos-contribuciones';
  if (tipo === 'activos_fijos') return '/motivos-activos-fijos';
  if (tipo === 'mercancias_nacionales') return '/motivos-mercancias-nacionales';
  if (tipo === 'nomina') return '/motivos-nomina';
  if (tipo === 'complemento_importacion') return '/motivos-complemento-importacion';
  return '/motivos';
};

// Documentos cuyo motivo real vive en los CONCEPTOS: `motivoPago` sólo guarda un
// resumen ("Nómina (3 conceptos)") que no existe en ningún catálogo, así que
// editarlo no reclasificaría nada. Se muestra, pero no se deja editar.
//
// Se pregunta por el CONTENIDO y no por el tipo: las contribuciones admiten tanto
// un motivo único —ahí sí es del catálogo y se puede corregir— como varios
// conceptos. Mismo criterio que tieneDesglosePorMotivo() en routes/solicitudes.js.
const CAMPO_CONCEPTOS = {
  nomina: 'conceptosNomina',
  debito: 'conceptosDebito',
  caja_chica: 'conceptosCajaChica',
  reembolso_gastos: 'conceptosReembolsoGastos',
  complemento_importacion: 'conceptosImportacion',
  contribuciones: 'conceptosContribuciones',
};
const motivoPorConcepto = (s) => {
  if (s?._coleccion === 'finiquito') return true;
  if (s?._coleccion !== 'solicitud') return false;
  const campo = CAMPO_CONCEPTOS[s.tipo];
  return !!campo && (s[campo] || []).length > 0;
};

export default function HistorialGeneral() {
  const { user } = useAuth();
  const [data, setData]       = useState({ items: [], total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [toast, setToast]     = useState(null);
  const [catalogos, setCatalogos] = useState({ empresas: [], sucursales: [] });
  // El Dashboard deja aquí un folio para abrirlo directo (se consume una sola vez)
  const [folioInicial] = useState(() => { try { return localStorage.getItem('historialFolioInicial') || ''; } catch { return ''; } });
  useEffect(() => { try { localStorage.removeItem('historialFolioInicial'); } catch {} }, []);
  const abrirAlCargar = useRef(folioInicial);
  const [filtros, setFiltros] = useState({
    estado: '', empresa: '', sucursal: '', tipo: '', desde: '', hasta: '', q: folioInicial,
    proveedor: '', direccion: '', solicitante: '',
    sortField: 'createdAt', sortDir: 'desc', page: 1,
  });
  const [pagoModal, setPagoModal] = useState({ open: false, id: null, col: null, files: [], yaTiene: false, fecha: '', tc: '', subiendo: false });
  const [progModal, setProgModal] = useState({ open: false, id: null, col: null, fecha: '', tc: '' });
  // Cuenta del proveedor por confirmar: se elige antes de programar o pagar
  const [confirmaCuenta, setConfirmaCuenta] = useState(null); // { sol, estado }
  // Tipos de cambio de referencia vigentes, para precargar los modales
  const [tcRef, setTcRef] = useState({});
  const [rechazoModal, setRechazoModal] = useState({ open: false, id: null, col: null, motivo: '' });
  const [cuentaModal, setCuentaModal] = useState(null); // { col, folio, motivo, cuentas, seleccion }
  const [envioModal, setEnvioModal] = useState(null);
  const [corregirModal, setCorregirModal] = useState(null);
  const [motivoQ, setMotivoQ] = useState('');
  const [rebasados, setRebasados] = useState({});
  const [detalle, setDetalle] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [comentario, setComentario] = useState('');
  const [enviandoComent, setEnviandoComent] = useState(false);
  const [subiendoComp, setSubiendoComp] = useState(false);
  const compInputRef = useRef(null);
  const comentarioRef = useRef(null);
  const puedeSubirComprobante = ['admin', 'programacion', 'finanzas'].includes(user?.rol);
  // Inputs con debounce para texto libre
  const [provInput, setProvInput]   = useState('');
  const [solicInput, setSolicInput] = useState('');
  const [qInput, setQInput]         = useState(folioInicial);
  const debRef = useRef({});

  const toast_ = (msg, tipo = 'ok') => { setToast({ msg, tipo }); setTimeout(() => setToast(null), 4000); };

  // Abrir detalle: carga el documento completo desde su colección
  const abrirDetalle = async (s) => {
    setLoadingDetalle(true);
    setDetalle({ ...s, _loading: true });
    setComentario('');
    try {
      const full = await api.get(`${epBase(s)}/${s._id}`);
      setDetalle({ ...full, _coleccion: s._coleccion });
    } catch { setDetalle(s); }
    finally { setLoadingDetalle(false); }
  };

  // Enviar comentario en el detalle
  const enviarComentario = async () => {
    if (!comentario.trim() || !detalle) return;
    setEnviandoComent(true);
    try {
      const r = await api.post(`${epBase(detalle)}/${detalle._id}/comentarios`, { texto: comentario });
      setDetalle(d => ({ ...d, comentarios: r.comentarios }));
      setComentario('');
      if (comentarioRef.current) comentarioRef.current.focus();
    } catch (e) { toast_(e.message, 'error'); }
    finally { setEnviandoComent(false); }
  };

  // Ver comprobante en el navegador (PDF/imagen)
  const verAdjunto = (fileId) => {
    const token = localStorage.getItem('token');
    window.open(`${BASE}${epBase(detalle)}/${detalle._id}/comprobantes/${fileId}/preview?token=${token}`, '_blank');
  };

  // Descargar comprobante
  const descargarAdjunto = async (fileId, nombre) => {
    try {
      const r = await api.downloadFile(`${epBase(detalle)}/${detalle._id}/comprobantes/${fileId}`);
      if (!r.ok) throw new Error('Archivo no disponible');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      Object.assign(document.createElement('a'), { href: url, download: nombre }).click();
      URL.revokeObjectURL(url);
    } catch (e) { toast_(e.message, 'error'); }
  };

  // Subir comprobante de pago desde el detalle
  const subirComprobante = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length || !detalle) return;
    setSubiendoComp(true);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('archivos', f));
      fd.append('concepto', 'Comprobante de pago');
      const res = await api.upload(`${epBase(detalle)}/${detalle._id}/comprobantes`, fd);
      setDetalle(d => ({ ...d, comprobantes: res.comprobantes }));
      toast_(`Comprobante subido (${res.subidos} archivo${res.subidos !== 1 ? 's' : ''})`);
      load();
    } catch (e) { toast_(e.message, 'error'); }
    finally { setSubiendoComp(false); if (compInputRef.current) compInputRef.current.value = ''; }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filtros).filter(([, v]) => v));
      const qs = new URLSearchParams(params).toString();
      setData(await api.get(`/historial-general?${qs}`));
    } catch (e) { toast_(e.message, 'error'); }
    finally { setLoading(false); }
  }, [filtros]);

  useEffect(() => { load(); }, [load]);

  // ZIP de todo lo filtrado: cada solicitud con su PDF y sus adjuntos, en carpetas
  // Compañía / Estado / Folio (ver GET /historial-general/zip). Primero se cuenta:
  // si pasa del tope, el servidor lo dice sin generar nada.
  const [zipEstado, setZipEstado] = useState(null); // null | { total }
  const descargarZip = async () => {
    const params = Object.fromEntries(Object.entries(filtros).filter(([k, v]) => v && !['page', 'sortField', 'sortDir'].includes(k)));
    const qs = new URLSearchParams(params).toString();
    try {
      const { total } = await api.get(`/historial-general/zip?${qs}${qs ? '&' : ''}soloContar=1`);
      setZipEstado({ total });
      const r = await api.downloadFile(`/historial-general/zip?${qs}`);
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'No se pudo generar el ZIP');
      const blob = await r.blob();
      const nombre = (r.headers.get('Content-Disposition') || '').match(/filename="([^"]+)"/)?.[1] || 'Solicitudes.zip';
      const url = URL.createObjectURL(blob);
      Object.assign(document.createElement('a'), { href: url, download: nombre }).click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast_(`ZIP descargado: ${total} ${total === 1 ? 'solicitud' : 'solicitudes'} con sus PDF y adjuntos`);
    } catch (e) { toast_(e.message, 'error'); }
    finally { setZipEstado(null); }
  };

  // Folio que llegó desde el Dashboard: al cargar la búsqueda, abre su detalle
  useEffect(() => {
    if (!abrirAlCargar.current || loading) return;
    const s = data.items.find(x => x.folio === abrirAlCargar.current);
    abrirAlCargar.current = '';
    if (s) abrirDetalle(s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, loading]);

  useEffect(() => {
    Promise.all([api.get('/empresas').catch(() => []), api.get('/sucursales').catch(() => [])])
      .then(([emp, suc]) => setCatalogos({ empresas: emp, sucursales: suc.map(s => s.nombre) }));
    api.get('/presupuestos/rebasados').then(d => setRebasados(d.afectados || {})).catch(() => {});
    api.get('/tipos-cambio/vigentes').then(setTcRef).catch(() => {});
  }, []);

  // Debounce para campos de texto libre
  useEffect(() => { clearTimeout(debRef.current.prov); debRef.current.prov = setTimeout(() => setFiltros(f => f.proveedor === provInput ? f : { ...f, proveedor: provInput, page: 1 }), 380); return () => clearTimeout(debRef.current.prov); }, [provInput]);
  useEffect(() => { clearTimeout(debRef.current.solic); debRef.current.solic = setTimeout(() => setFiltros(f => f.solicitante === solicInput ? f : { ...f, solicitante: solicInput, page: 1 }), 380); return () => clearTimeout(debRef.current.solic); }, [solicInput]);
  useEffect(() => { clearTimeout(debRef.current.q); debRef.current.q = setTimeout(() => setFiltros(f => f.q === qInput ? f : { ...f, q: qInput, page: 1 }), 380); return () => clearTimeout(debRef.current.q); }, [qInput]);

  const setF = (k, v) => setFiltros(f => ({ ...f, [k]: v, page: 1 }));

  // ¿El usuario puede corregir y reenviar esta solicitud?
  const puedeCorregir = (s) =>
    s.estado === 'rechazada' &&
    (user?.rol === 'admin' ||
      String(s.usuario?._id || s.usuario) === String(user?._id));

  const DIRECCIONES_FIJAS = [
    'DIRECCION GENERAL','DIRECCION COMERCIAL','DIRECCION COMPRAS','DIRECCION JURIDICO RH',
    'DIRECCION FINANZAS','DIRECCION UNE CRISTALERIA','DIRECCION UNE MEDICAL STORE',
    'DIRECCION LOGISTICA','DIRECCION GOLDEN','DIRECCION PATRIMONIAL','DIRECCION FONTASTIC',
  ];

  const abrirCorregir = async (s) => {
    // Carga catálogos de sucursales y centros de costos para la empresa/sucursal actual
    const [sucursalesAll, centrosAll, motivosAll] = await Promise.all([
      api.get('/sucursales').catch(() => []),
      s.sucursal ? api.get(`/centros?sucursal=${encodeURIComponent(s.sucursal)}`).catch(() => []) : Promise.resolve([]),
      // El catálogo depende del tipo del documento (débito, nómina, importación...)
      motivoPorConcepto(s) ? Promise.resolve([]) : api.get(catalogoMotivos(s)).catch(() => []),
    ]);
    setMotivoQ('');
    setCorregirModal({
      col: s, id: s._id, folio: s.folio, motivoRechazo: s.motivoRechazo || '',
      nota: '', enviando: false,
      sucursalesOpc: sucursalesAll.map(x => x.nombre || x),
      centrosOpc: centrosAll.map(x => x.nombre || x),
      motivosOpc: (motivosAll || []).map(m => m.nombre).filter(Boolean),
      motivoFijo: motivoPorConcepto(s),
      // El destino del pago se muestra pero no se edita ni se envía: sólo lectura
      destino: {
        beneficiario: s.proveedor || s.comprobador || s.beneficiario || '',
        banco: s.banco || '', clabe: s.clabe || '', cuenta: s.cuenta || '',
      },
      campos: {
        monto: String(s.monto ?? ''), folioCfdi: s.folioCfdi || '',
        observaciones: s.observaciones || '', motivoPago: s.motivoPago || '',
        direccion: s.direccion || '', sucursal: s.sucursal || '',
        centroCostos: s.centroCostos || '',
      },
    });
  };

  // Recarga centros cuando cambia la sucursal en el modal de corrección
  const cambiarSucursalCorregir = async (val) => {
    setCorregirModal(m => ({ ...m, campos: { ...m.campos, sucursal: val, centroCostos: '' }, centrosOpc: [] }));
    if (val) {
      const centros = await api.get(`/centros?sucursal=${encodeURIComponent(val)}`).catch(() => []);
      setCorregirModal(m => ({ ...m, centrosOpc: centros.map(x => x.nombre || x) }));
    }
  };

  const confirmarReenvio = async () => {
    const { col, id, campos, nota } = corregirModal;
    if (!(parseFloat(campos.monto) > 0)) {
      toast_('El monto es obligatorio', 'error'); return;
    }
    setCorregirModal(m => ({ ...m, enviando: true }));
    try {
      await api.post(`${epBase(col)}/${id}/reenviar`, {
        ...campos, monto: parseFloat(campos.monto) || 0, nota,
      });
      toast_('Solicitud corregida y reenviada — vuelve a estar en Validación Contable');
      setCorregirModal(null);
      load();
    } catch (e) { toast_(e.message, 'error'); setCorregirModal(m => ({ ...m, enviando: false })); }
  };

  // Borrar solicitud (admin/contador) — pide motivo por prompt
  const handleDelete = async (s) => {
    if (!['admin', 'contador'].includes(user?.rol)) return;
    const motivo = window.prompt('Motivo de borrado (obligatorio):');
    if (motivo === null) return;
    if (!motivo.trim()) { toast_('El motivo de borrado es obligatorio', 'error'); return; }
    try {
      await api.del(`${epBase(s)}/${s._id}`, { motivo: motivo.trim() });
      toast_('Registro borrado');
      load();
    } catch (e) { toast_(e.message, 'error'); }
  };

  // Ordenamiento: alterna dirección si es el mismo campo, o pone desc para uno nuevo
  const toggleSort = (field) => setFiltros(f => ({
    ...f, sortField: field,
    sortDir: f.sortField === field && f.sortDir === 'desc' ? 'asc' : 'desc',
    page: 1,
  }));

  const SortTh = ({ field, children, style = {} }) => {
    const active = filtros.sortField === field;
    return (
      <th onClick={() => toggleSort(field)} style={{
        padding: '10px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: active ? 'var(--plasma)' : 'var(--glass)',
        textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap', cursor: 'pointer',
        userSelect: 'none', transition: 'color 0.12s', ...style,
      }}>
        {children} <span style={{ fontSize: 9, opacity: active ? 1 : 0.4 }}>{active ? (filtros.sortDir === 'desc' ? '▼' : '▲') : '⇅'}</span>
      </th>
    );
  };

  // Cambio de estado enrutado por colección
  const cambiarEstado = async (s, estado, motivo = '', extra = {}) => {
    try {
      await api.patch(`${epBase(s)}/${s._id}/estado`, { estado, motivo, ...extra });
      toast_(`Estado actualizado: ${ESTADO_LABEL[estado] || estado}`);
      load();
    } catch (e) { toast_(e.message, 'error'); }
  };

  // Obtiene las cuentas contables de un motivo según el tipo de solicitud
  const cuentasDelMotivo = async (s, nombreMotivo) => {
    try {
      const ep = catalogoMotivos(s);
      const lista = await api.get(`${ep}?q=${encodeURIComponent(nombreMotivo || '')}`);
      const mot = (lista || []).find(m => m.nombre?.toLowerCase() === (nombreMotivo || '').trim().toLowerCase());
      return mot?.cuentasContables || [];
    } catch { return []; }
  };

  // Aprobar con selección de cuenta contable (adaptado de Historial.js)
  const aprobarConCuenta = async (s) => {
    try {
      const sol = await api.patch(`${epBase(s)}/${s._id}/estado`, { estado: 'aprobada', motivo: '' });
      toast_(`Estado actualizado: ${ESTADO_LABEL.aprobada}`);
      if (detalle?._id === s._id) setDetalle(d => ({ ...d, ...sol }));
      load();
    } catch (e) {
      if ((e.message || '').includes('Selecciona la cuenta contable')) {
        const cuentas = await cuentasDelMotivo(s, s.motivoPago);
        setCuentaModal({ open: true, col: s, id: s._id, folio: s.folio, motivo: s.motivoPago, cuentas, seleccion: '', modo: 'aprobar' });
      } else { toast_(e.message, 'error'); }
    }
  };

  // Editar la cuenta contable desde el detalle (contador/admin)
  const abrirEditarCuenta = async () => {
    if (!detalle) return;
    const cuentas = await cuentasDelMotivo(detalle, detalle.motivoPago);
    if (!cuentas.length) { toast_('El motivo de esta solicitud no tiene cuentas en el catálogo', 'error'); return; }
    setCuentaModal({ open: true, col: detalle, id: detalle._id, folio: detalle.folio, motivo: detalle.motivoPago, cuentas, seleccion: detalle.cuentaContable || '', modo: 'editar' });
  };

  const confirmarCuenta = async () => {
    const { id, col, seleccion, modo } = cuentaModal || {};
    if (!seleccion) { toast_('Selecciona una cuenta contable', 'error'); return; }
    if (modo === 'aprobar') {
      await cambiarEstado(col, 'aprobada', '', { cuentaContable: seleccion });
    } else {
      try {
        await api.patch(`${epBase(col)}/${id}/cuenta-contable`, { cuentaContable: seleccion });
        toast_('Cuenta contable actualizada');
        if (detalle?._id === id) setDetalle(d => ({ ...d, cuentaContable: seleccion }));
        load();
      } catch (e) { toast_(e.message, 'error'); return; }
    }
    setCuentaModal(null);
  };

  const handleEstado = (s, estado) => {
    if (['por_pagar', 'pagada'].includes(estado) && s.cuentaPorConfirmar) {
      setConfirmaCuenta({ sol: s, estado });
      return;
    }
    if (estado === 'rechazada') {
      setRechazoModal({ open: true, id: s._id, col: s, motivo: '' });
    } else if (estado === 'pagada') {
      const yaTiene = (s.comprobantes || []).some(a => a.concepto === 'Comprobante de pago');
      // Precargado con el que puso programación: si el banco cobró lo pactado, es un Enter
      setPagoModal({ open: true, id: s._id, col: s, cuentaPago: '', files: [], yaTiene, fecha: '', subiendo: false,
        tc: String(s.tipoCambioPago || s.tipoCambio || tcRef[s.moneda] || '') });
    } else if (estado === 'por_pagar') {
      const hoy = new Date().toLocaleDateString('en-CA');
      setProgModal({ open: true, id: s._id, col: s, fecha: hoy, tc: String(s.tipoCambio || tcRef[s.moneda] || '') });
    } else if (estado === 'aprobada') {
      aprobarConCuenta(s);
    } else {
      cambiarEstado(s, estado);
    }
  };

  const confirmarPago = async () => {
    const { id, col, files, yaTiene, fecha, tc } = pagoModal;
    if (!files.length && !yaTiene) { toast_('Adjunta el comprobante de pago', 'error'); return; }
    if (!fecha) { toast_('Indica la fecha en que se hizo el pago', 'error'); return; }
    if (fecha > new Date().toLocaleDateString('en-CA')) { toast_('La fecha de pago no puede ser futura', 'error'); return; }
    if (esExtranjera(col) && !(parseFloat(tc) > 0)) { toast_('Indica el tipo de cambio real del pago', 'error'); return; }
    setPagoModal(m => ({ ...m, subiendo: true }));
    try {
      if (files.length) {
        const fd = new FormData();
        files.forEach(f => fd.append('archivos', f));
        fd.append('concepto', 'Comprobante de pago');
        await api.upload(`${epBase(col)}/${id}/comprobantes`, fd);
      }
      await cambiarEstado(col, 'pagada', '', { fechaPago: fecha, tipoCambioPago: parseFloat(tc) || undefined, cuentaPago: pagoModal.cuentaPago || undefined });
      setPagoModal({ open: false, id: null, col: null, files: [], yaTiene: false, fecha: '', tc: '', subiendo: false });
    } catch (e) { toast_(e.message, 'error'); setPagoModal(m => ({ ...m, subiendo: false })); }
  };

  // Envío del comprobante de pago al proveedor. Es manual a propósito: un correo no
  // se puede deshacer y el comprobante lleva datos del pago, así que el destinatario
  // se muestra (y se puede corregir) antes de enviar.
  const abrirEnvio = async (s) => {
    setEnvioModal({ open: true, id: s._id, folio: s.folio, proveedor: s.proveedor, cargando: true, email: '', enviando: false });
    try {
      const d = await api.get(`/solicitudes/${s._id}/destinatario`);
      setEnvioModal(m => m && m.id === s._id ? { ...m, ...d, email: d.email || '', cargando: false } : m);
    } catch (e) {
      setEnvioModal(m => m && m.id === s._id ? { ...m, cargando: false, error: e.message } : m);
    }
  };

  const confirmarEnvio = async () => {
    const { id, email } = envioModal;
    setEnvioModal(m => ({ ...m, enviando: true }));
    try {
      const r = await api.post(`/solicitudes/${id}/enviar-comprobante`, { email });
      toast_(`Comprobante enviado a ${r.email}`);
      setEnvioModal(null);
      load();
    } catch (e) {
      toast_(e.message, 'error');
      setEnvioModal(m => m && { ...m, enviando: false });
    }
  };

  const confirmarProg = async () => {
    const { col, fecha, tc } = progModal;
    if (!fecha) { toast_('Indica la fecha de programación', 'error'); return; }
    if (esExtranjera(col) && !(parseFloat(tc) > 0)) { toast_('Indica el tipo de cambio', 'error'); return; }
    await cambiarEstado(col, 'por_pagar', '', { fechaProgramacion: fecha, tipoCambio: parseFloat(tc) || undefined });
    setProgModal({ open: false, id: null, col: null, fecha: '', tc: '' });
  };

  const confirmarRechazo = async () => {
    if (!rechazoModal.motivo.trim()) { toast_('El motivo de rechazo es obligatorio', 'error'); return; }
    await cambiarEstado(rechazoModal.col, 'rechazada', rechazoModal.motivo);
    setRechazoModal({ open: false, id: null, col: null, motivo: '' });
  };

  const handlePDF = async (s) => {
    try {
      const blob = await api.downloadPdf(`${epBase(s)}/${s._id}/pdf`);
      const url = URL.createObjectURL(blob);
      Object.assign(document.createElement('a'), { href: url, download: `${s.folio}.pdf` }).click();
      URL.revokeObjectURL(url);
    } catch (e) { toast_(e.message, 'error'); }
  };

  const empresaObj = catalogos.empresas.find(e => e.nombre === filtros.empresa);
  const sucursalesVisibles = empresaObj?.sucursales?.length
    ? catalogos.sucursales.filter(s => empresaObj.sucursales.includes(s))
    : catalogos.sucursales;

  const SELECT = { padding: '7px 10px', borderRadius: 9, border: '1px solid var(--hair)', background: 'var(--panel)', fontSize: 12.5, fontFamily: 'Inter, sans-serif', cursor: 'pointer' };

  return (
    <div style={{ fontFamily: 'Inter, sans-serif' }}>
      <Toast msg={toast?.msg} tipo={toast?.tipo} onClose={() => setToast(null)} />

      <PageHeader icon="📋" title="Historial General"
        subtitle={`${data.total} registros · solicitudes, reembolsos y finiquitos`}
        actions={<>
          <button onClick={descargarZip} disabled={!!zipEstado || !data.total}
            title="Descarga en un ZIP todas las solicitudes del filtro actual: su PDF, sus adjuntos y, si vienen de una requisición, sus documentos de compra, en carpetas Compañía / Estado / Folio, con un resumen en Excel de cada factura"
            style={{ ...css.btnGhost, border: '1px solid var(--hair)', fontSize: 12, opacity: zipEstado ? 0.7 : 1 }}>
            {zipEstado ? `Generando ZIP (${zipEstado.total})…` : '⬇️ Descargar ZIP'}
          </button>
          <button onClick={load} style={{ ...css.btnGhost, border: '1px solid var(--hair)', fontSize: 12 }}>🔄 Actualizar</button>
        </>}
      />

      {/* Filtros */}
      <div style={{ ...css.card, padding: '12px 16px', marginBottom: 16 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <select value={filtros.tipo} onChange={e => setF('tipo', e.target.value)} style={SELECT}>
            <option value="">📄 Todos los tipos</option>
            {Object.entries(TIPO_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
          </select>
          <select value={filtros.estado} onChange={e => setF('estado', e.target.value)} style={SELECT}>
            <option value="">Estado: todos</option>
            {['pendiente','aprobada','por_pagar','rechazada','pagada'].map(e => <option key={e} value={e}>{ESTADO_LABEL[e] || e}</option>)}
          </select>
          <select value={filtros.empresa} onChange={e => setF('empresa', e.target.value)} style={SELECT}>
            <option value="">🏢 Compañía</option>
            {catalogos.empresas.map(e => <option key={e._id || e.nombre} value={e.nombre}>{e.nombre}</option>)}
          </select>
          <select value={filtros.sucursal} onChange={e => setF('sucursal', e.target.value)} style={SELECT}>
            <option value="">📍 Sucursal</option>
            {sucursalesVisibles.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <input value={provInput} onChange={e => setProvInput(e.target.value)} placeholder="🏷️ Proveedor…" style={{ ...SELECT, width: 140 }} />
          {!['solicitante'].includes(user?.rol) && (
            <input value={solicInput} onChange={e => setSolicInput(e.target.value)} placeholder="👤 Solicitante…" style={{ ...SELECT, width: 140 }} />
          )}
          <input type="date" value={filtros.desde} onChange={e => setF('desde', e.target.value)} style={{ ...SELECT, width: 128 }} />
          <span style={{ color: 'var(--glass)', fontSize: 11 }}>→</span>
          <input type="date" value={filtros.hasta} onChange={e => setF('hasta', e.target.value)} style={{ ...SELECT, width: 128 }} />
          <input value={qInput} onChange={e => setQInput(e.target.value)} placeholder="🔍 Folio…" style={{ ...SELECT, width: 120 }} />
          {(Object.entries(filtros).some(([k, v]) => v && !['sortField','sortDir','page'].includes(k)) || provInput || solicInput || qInput) && (
            <button onClick={() => {
              setFiltros({ estado: '', empresa: '', sucursal: '', tipo: '', desde: '', hasta: '', q: '', proveedor: '', direccion: '', solicitante: '', sortField: 'createdAt', sortDir: 'desc', page: 1 });
              setProvInput(''); setSolicInput(''); setQInput('');
            }} style={{ padding: '6px 12px', borderRadius: 9, border: '1px solid rgba(239,68,68,0.28)', background: 'rgba(239,68,68,0.10)', color: 'var(--danger)', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
              ✕ Limpiar
            </button>
          )}
        </div>
      </div>

      {loading && !data.items.length && <div style={{ padding: 60, display: 'flex', justifyContent: 'center' }}><Spinner /></div>}

      <div style={{ ...css.card, padding: 0, overflow: 'hidden', opacity: loading ? 0.6 : 1, transition: 'opacity 0.15s' }}>
        <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: 900, borderCollapse: 'collapse', fontSize: 12.5 }}>
          <thead>
            <tr style={{ background: 'var(--hair-soft)', borderBottom: '1px solid var(--hair)' }}>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>Folio</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>Tipo</th>
              <SortTh field="empresa">Compañía</SortTh>
              <SortTh field="proveedor">Proveedor</SortTh>
              <SortTh field="monto">Monto</SortTh>
              <SortTh field="estado">Estado</SortTh>
              <SortTh field="createdAt">Fecha</SortTh>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>Solicitante</th>
              <th style={{ padding: '10px 14px' }}></th>
            </tr>
          </thead>
          <tbody>
            {data.items.length === 0 && !loading && (
              <tr><td colSpan={9} style={{ padding: 40, textAlign: 'center', color: 'var(--glass)', fontSize: 13 }}>Sin registros con los filtros actuales</td></tr>
            )}
            {data.items.map(s => {
              const tipo = tipoDeItem(s);
              const tm = TIPO_META[tipo] || TIPO_META.pago;
              const monto = parseFloat(s.montoNeto ?? s.monto ?? 0);
              const alertaP = rebasados[s.centroCostos];
              return (
                <tr key={`${s._coleccion}-${s._id}`}
                  style={{ borderBottom: '1px solid var(--hair-soft)', transition: 'background 0.12s' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,84,28,0.04)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>

                  {/* Folio */}
                  <td style={{ padding: '10px 14px', whiteSpace: 'nowrap', minWidth: 160 }}>
                    <span style={{ fontFamily: 'Geist Mono, monospace', fontSize: 11.5, color: 'var(--plasma)', fontWeight: 700, background: 'rgba(255,84,28,0.08)', padding: '2px 8px', borderRadius: 5, border: '1px solid rgba(255,84,28,0.20)' }}>
                      {s.folio}
                    </span>
                    {s.comentariosNuevos > 0 && (
                      <span style={{ marginLeft: 6, padding: '2px 7px', borderRadius: 99, fontSize: 10, fontWeight: 800, background: 'rgba(239,68,68,0.10)', color: 'var(--danger)', border: '1px solid rgba(239,68,68,0.28)' }}>
                        💬 {s.comentariosNuevos}
                      </span>
                    )}
                    {alertaP && <span title={`Presupuesto rebasado`} style={{ marginLeft: 4, fontSize: 10, color: 'var(--danger)', fontWeight: 700 }}>🚨</span>}
                  </td>

                  {/* Tipo */}
                  <td style={{ padding: '10px 14px' }}>
                    <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 10.5, fontWeight: 700, background: tm.bg, color: tm.color, border: `1px solid ${tm.color}40`, whiteSpace: 'nowrap' }}>
                      {tm.label}
                    </span>
                  </td>

                  {/* Empresa */}
                  <td style={{ padding: '10px 14px', fontWeight: 600, maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--ink)' }}>{s.empresa}</td>

                  {/* Proveedor */}
                  <td style={{ padding: '10px 14px', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--ink-soft)', fontSize: 12 }}>
                    {s.proveedor || s.comprobador || '—'}
                  </td>

                  {/* Monto */}
                  <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                    <span style={{ fontWeight: 700, color: 'var(--ink)', fontFamily: 'Geist Mono, monospace', fontSize: 12 }}>${fmt(monto)}</span>
                    <span style={{ fontSize: 10.5, color: 'var(--glass)', marginLeft: 4 }}>{s.moneda || 'MXN'}</span>
                    {/* En moneda extranjera, debajo el equivalente en pesos que es lo que
                        suma el tablero, marcado según de dónde salió el tipo de cambio */}
                    {esExtranjera(s) && (() => {
                      const o = TC_ORIGEN[s.origenTipoCambio] || TC_ORIGEN.estimado;
                      return (
                        <div title={`${o.titulo} · 1 ${s.moneda} = $${fmt(s.tipoCambioAplicado)}`}
                          style={{ fontSize: 10.5, color: o.color, fontFamily: 'Geist Mono, monospace', marginTop: 1 }}>
                          ≈ ${fmt(s.montoNetoMXN)} MXN <span style={{ fontFamily: 'Inter, sans-serif' }}>({o.label})</span>
                        </div>
                      );
                    })()}
                  </td>

                  {/* Estado */}
                  <td style={{ padding: '10px 14px' }}>
                    {(() => {
                      const ops = opcionesEstado(user?.rol, s);
                      return ops ? (
                        <select value={s.estado} onChange={e => handleEstado(s, e.target.value)}
                          style={{ padding: '4px 10px', borderRadius: 20, border: `1.5px solid ${ESTADO_COLORS[s.estado]}40`, background: `${ESTADO_COLORS[s.estado]}14`, color: ESTADO_COLORS[s.estado], fontSize: 11.5, fontWeight: 700, fontFamily: 'Inter, sans-serif', cursor: 'pointer', outline: 'none', appearance: 'none', paddingRight: 20, backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24'%3E%3Cpath fill='%2394a3b8' d='M7 10l5 5 5-5z'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 4px center' }}>
                          {ops.map(e => <option key={e} value={e}>{ESTADO_OPCION_LABEL[e] || e}</option>)}
                        </select>
                      ) : <EstadoBadge estado={s.estado} />;
                    })()}
                    {(s.cuentaPorConfirmar || s.cuentaNueva) && <div style={{ marginTop: 3 }}><AvisoCuentaProveedor s={s} compacto /></div>}
                    {s.fechaProgramacion && ['por_pagar','pagada'].includes(s.estado) && (
                      <div style={{ fontSize: 10, color: '#0ea5e9', marginTop: 3, fontFamily: 'Geist Mono, monospace' }}>📅 {isoLocal(s.fechaProgramacion)}</div>
                    )}
                  </td>

                  {/* Fecha */}
                  <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--glass)', whiteSpace: 'nowrap' }}>
                    {new Date(s.createdAt).toLocaleDateString('es-MX')}
                  </td>

                  {/* Solicitante */}
                  <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--ink-soft)', maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {s.usuario?.nombre || '—'}
                  </td>

                  {/* Acciones */}
                  <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', gap: 5 }}>
                      <IconBtn onClick={() => abrirDetalle(s)} title="Ver detalle" icon="👁" />
                      <IconBtn onClick={() => handlePDF(s)} title="Descargar PDF" icon="⬇️" />
                      {puedeSubirComprobante && (() => {
                        const tieneComp = (s.comprobantes || []).some(c => c.concepto === 'Comprobante de pago');
                        return (
                        <span title={tieneComp ? '✓ Comprobante de pago ya subido — clic para reemplazar' : 'Subir comprobante de pago'}
                          style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 7, border: `1.5px solid ${tieneComp ? 'rgba(22,163,74,0.45)' : 'var(--hair)'}`, background: tieneComp ? 'rgba(22,163,74,0.12)' : 'var(--hair-soft)', cursor: 'pointer', fontSize: 13, transition: 'all 0.15s' }}
                          onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.12)'; }}
                          onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
                          onClick={() => {
                            const inp = document.createElement('input');
                            inp.type = 'file'; inp.multiple = true;
                            inp.accept = '.pdf,.jpg,.jpeg,.png,.webp';
                            inp.onchange = async (e) => {
                              const files = Array.from(e.target.files || []);
                              if (!files.length) return;
                              try {
                                const fd = new FormData();
                                files.forEach(f => fd.append('archivos', f));
                                fd.append('concepto', 'Comprobante de pago');
                                await api.upload(`${epBase(s)}/${s._id}/comprobantes`, fd);
                                toast_('Comprobante subido correctamente');
                                load();
                              } catch (err) { toast_(err.message, 'error'); }
                            };
                            inp.click();
                          }}>
                          {tieneComp ? '✓' : '📎'}
                        </span>
                        );
                      })()}
                      {puedeCorregir(s) && (
                        <IconBtn onClick={() => abrirCorregir(s)} title="Corregir y reenviar" icon="🔁" />
                      )}
                      {/* Solo la colección `solicitud`: las demás no tienen esta ruta todavía */}
                      {s.estado === 'pagada' && s._coleccion === 'solicitud' && ['admin', 'programacion', 'finanzas'].includes(user?.rol) && (
                        <IconBtn onClick={() => abrirEnvio(s)}
                          title={(s.enviosComprobante || []).some(e => e.ok)
                            ? 'Comprobante ya enviado al proveedor — clic para reenviar'
                            : 'Enviar el comprobante de pago al proveedor'}
                          icon={(s.enviosComprobante || []).some(e => e.ok) ? '📨' : '✉️'} />
                      )}
                      {['admin', 'contador'].includes(user?.rol) && (
                        <IconBtn onClick={() => handleDelete(s)} title="Eliminar" icon="🗑" danger />
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>

      <div style={{ marginTop: 12 }}>
        <Paginacion page={filtros.page} pages={data.pages} onPage={p => setFiltros(f => ({ ...f, page: p }))} />
      </div>

      {/* Modal comprobante de pago */}
      <Modal open={pagoModal.open} onClose={() => setPagoModal(m => ({ ...m, open: false }))} title="💵 Marcar como Pagada" width={460}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {pagoModal.yaTiene && (
            <div style={{ fontSize: 12, color: 'var(--ok)', background: 'rgba(22,163,74,0.10)', border: '1px solid rgba(22,163,74,0.28)', borderRadius: 8, padding: '8px 12px' }}>
              ✓ Ya tiene un comprobante de pago adjunto; puedes confirmar sin subir otro.
            </div>
          )}
          <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png"
            onChange={e => setPagoModal(m => ({ ...m, files: Array.from(e.target.files || []) }))} style={{ fontSize: 12.5 }} />
          <SelectorCuentaPago empresa={pagoModal.col?.empresa} value={pagoModal.cuentaPago} onChange={v => setPagoModal(m => ({ ...m, cuentaPago: v }))} />
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5, color: 'var(--ink-soft)' }}>
            <span style={{ fontWeight: 600 }}>Fecha de pago</span>
            <input type="date" value={pagoModal.fecha} max={new Date().toLocaleDateString('en-CA')} required onChange={e => setPagoModal(m => ({ ...m, fecha: e.target.value }))}
              style={{ fontSize: 13, padding: '7px 10px', border: '1px solid var(--hair-strong)', borderRadius: 8 }} />
          </label>
          {esExtranjera(pagoModal.col) && (() => {
            const tc = parseFloat(pagoModal.tc) || 0;
            const base = parseFloat(pagoModal.col?.montoNeto ?? pagoModal.col?.monto) || 0;
            return (
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5, color: 'var(--ink-soft)' }}>
                <span style={{ fontWeight: 600 }}>Tipo de cambio real del pago (pesos por 1 {pagoModal.col.moneda})</span>
                <input type="number" step="0.0001" min="0" value={pagoModal.tc} placeholder="17.4523"
                  onChange={e => setPagoModal(m => ({ ...m, tc: e.target.value }))}
                  style={{ fontSize: 13, padding: '7px 10px', border: '1px solid var(--hair-strong)', borderRadius: 8, fontFamily: 'Geist Mono, monospace' }} />
                <span style={{ fontSize: 11.5, color: tc > 0 ? 'var(--ok)' : 'var(--st-pendiente)' }}>
                  {tc > 0
                    ? `${pagoModal.col.moneda} ${fmt(base)} \u2192 $${fmt(base * tc)} MXN`
                    : 'Viene precargado con el que se program\u00f3; corr\u00edgelo si el banco cobr\u00f3 otro'}
                </span>
              </label>
            );
          })()}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={() => setPagoModal(m => ({ ...m, open: false }))} style={{ ...css.btn('#6b7280', true), fontSize: 13 }}>Cancelar</button>
            <button onClick={confirmarPago} disabled={pagoModal.subiendo} style={{ ...css.btn('#3b82f6'), fontSize: 13, opacity: pagoModal.subiendo ? 0.6 : 1 }}>
              {pagoModal.subiendo ? 'Subiendo…' : '💵 Confirmar pago'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal fecha de programación */}
      {/* Envío del comprobante de pago al proveedor */}
      <Modal open={!!envioModal} onClose={() => setEnvioModal(null)} title={`✉️ Enviar comprobante ${envioModal?.folio || ''}`} width={480}>
        {envioModal?.cargando ? <Spinner /> : envioModal && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {envioModal.configurado === false && (
              <div style={{ padding: '10px 12px', background: 'var(--danger-bg)', border: '1px solid #fecaca', borderRadius: 8, fontSize: 12.5, color: 'var(--danger)' }}>
                El envío de correo no está configurado en el servidor. Define <b>SMTP_USER</b> y <b>SMTP_PASS</b> antes de usar esta opción.
              </div>
            )}
            {envioModal.configurado !== false && !envioModal.encontrado && (
              <div style={{ padding: '10px 12px', background: 'rgba(245,207,74,0.08)', border: '1px solid #92400e', borderRadius: 8, fontSize: 12.5, color: 'var(--st-pendiente)' }}>
                <b>{envioModal.proveedor}</b> no está en el catálogo de proveedores, así que no se pudo tomar su correo. Captúralo abajo.
              </div>
            )}
            {envioModal.configurado !== false && envioModal.encontrado && !envioModal.email && (
              <div style={{ padding: '10px 12px', background: 'rgba(245,207,74,0.08)', border: '1px solid #92400e', borderRadius: 8, fontSize: 12.5, color: 'var(--st-pendiente)' }}>
                <b>{envioModal.proveedor}</b> no tiene correo capturado en el catálogo. Escríbelo abajo, y conviene guardarlo en el catálogo para la próxima.
              </div>
            )}

            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Enviar a</div>
              <input value={envioModal.email} onChange={e => setEnvioModal(m => ({ ...m, email: e.target.value }))}
                placeholder="correo@proveedor.com" style={{ ...css.input(false), width: '100%', fontFamily: 'Geist Mono, monospace' }} />
              <div style={{ fontSize: 11.5, color: 'var(--glass)', marginTop: 5 }}>
                Revisa la dirección antes de enviar: el comprobante lleva datos del pago y un correo no se puede recuperar.
              </div>
            </div>

            <div style={{ fontSize: 12.5, color: 'var(--ink-soft)', background: 'var(--hair-soft)', borderRadius: 8, padding: '9px 12px' }}>
              📎 Se adjunta: <b>{envioModal.archivo || '— sin comprobante —'}</b>
            </div>

            {(envioModal.envios || []).length > 0 && (
              <div style={{ borderTop: '1px solid var(--hair)', paddingTop: 10 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Envíos anteriores</div>
                {envioModal.envios.slice().reverse().map((e, i) => (
                  <div key={i} style={{ fontSize: 11.5, color: e.ok ? 'var(--ok)' : 'var(--danger)', marginBottom: 3 }}>
                    {e.ok ? '✓' : '✗'} {e.email} · {new Date(e.fecha).toLocaleString('es-MX')} · {e.nombreUsuario}
                    {!e.ok && e.error && <div style={{ color: 'var(--danger)', fontSize: 11, paddingLeft: 14 }}>{e.error}</div>}
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setEnvioModal(null)} style={css.btn('#6b7280', true)}>Cancelar</button>
              <button onClick={confirmarEnvio}
                disabled={envioModal.enviando || !envioModal.email || !envioModal.archivo || envioModal.configurado === false}
                style={{ ...css.btn('#15803d'), fontSize: 13, opacity: (envioModal.enviando || !envioModal.email || !envioModal.archivo || envioModal.configurado === false) ? 0.5 : 1 }}>
                {envioModal.enviando ? 'Enviando…' : '✉️ Enviar comprobante'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      <ModalConfirmarCuenta solicitud={confirmaCuenta?.sol} onClose={() => setConfirmaCuenta(null)}
        onConfirmada={(cambios) => {
          const { sol, estado } = confirmaCuenta;
          const nueva = { ...sol, ...cambios };
          setData(d => ({ ...d, items: d.items.map(i => (i._id === sol._id ? { ...i, ...cambios } : i)) }));
          if (detalle?._id === sol._id) setDetalle(d => ({ ...d, ...cambios }));
          setConfirmaCuenta(null);
          toast_('Cuenta confirmada');
          if (estado) handleEstado(nueva, estado);
        }} />

      <Modal open={progModal.open} onClose={() => setProgModal(m => ({ ...m, open: false }))} title="🗓️ Programar pago" width={400}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5, color: 'var(--ink-soft)' }}>
            <span style={{ fontWeight: 600 }}>Fecha de programación</span>
            <input type="date" value={progModal.fecha} onChange={e => setProgModal(m => ({ ...m, fecha: e.target.value }))}
              style={{ fontSize: 13, padding: '7px 10px', border: '1px solid var(--hair-strong)', borderRadius: 8 }} />
          </label>
          {esExtranjera(progModal.col) && (() => {
            const tc = parseFloat(progModal.tc) || 0;
            const base = parseFloat(progModal.col?.montoNeto ?? progModal.col?.monto) || 0;
            return (
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5, color: 'var(--ink-soft)' }}>
                <span style={{ fontWeight: 600 }}>Tipo de cambio (pesos por 1 {progModal.col.moneda})</span>
                <input type="number" step="0.0001" min="0" value={progModal.tc} placeholder="17.4523"
                  onChange={e => setProgModal(m => ({ ...m, tc: e.target.value }))}
                  style={{ fontSize: 13, padding: '7px 10px', border: '1px solid var(--hair-strong)', borderRadius: 8, fontFamily: 'Geist Mono, monospace' }} />
                <span style={{ fontSize: 11.5, color: tc > 0 ? 'var(--ok)' : 'var(--st-pendiente)' }}>
                  {tc > 0
                    ? `${progModal.col.moneda} ${fmt(base)} \u2192 $${fmt(base * tc)} MXN`
                    : 'Obligatorio: con \u00e9l se convierte a pesos en el tablero y los reportes'}
                </span>
              </label>
            );
          })()}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={() => setProgModal(m => ({ ...m, open: false }))} style={{ ...css.btn('#6b7280', true), fontSize: 13 }}>Cancelar</button>
            <button onClick={confirmarProg} style={{ ...css.btn('#00b8c4'), fontSize: 13 }}>🗓️ Confirmar</button>
          </div>
        </div>
      </Modal>

      {/* Modal rechazo */}
      <Modal open={rechazoModal.open} onClose={() => setRechazoModal(m => ({ ...m, open: false }))} title="❌ Rechazar solicitud" width={420}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <textarea rows={3} value={rechazoModal.motivo} onChange={e => setRechazoModal(m => ({ ...m, motivo: e.target.value }))}
            placeholder="Motivo de rechazo (obligatorio)..." style={{ ...css.input(!rechazoModal.motivo.trim()), resize: 'vertical' }} />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={() => setRechazoModal(m => ({ ...m, open: false }))} style={{ ...css.btn('#6b7280', true), fontSize: 13 }}>Cancelar</button>
            <button onClick={confirmarRechazo} style={{ ...css.btn('#dc2626'), fontSize: 13 }}>❌ Rechazar</button>
          </div>
        </div>
      </Modal>

      {/* Modal corregir y reenviar */}
      <Modal open={!!corregirModal} onClose={() => setCorregirModal(null)} title={`🔁 Corregir y reenviar — ${corregirModal?.folio || ''}`} width={560}>
        {corregirModal && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, fontSize: 13 }}>
            {corregirModal.motivoRechazo && (
              <div style={{ padding: '8px 12px', background: 'var(--danger-bg)', borderRadius: 4, border: '1px solid #be123c', color: 'var(--danger)', fontSize: 12.5 }}>
                <b>Motivo del rechazo:</b> {corregirModal.motivoRechazo}
              </div>
            )}
            <DestinoPagoFijo campos={[
              [catalogoDe(corregirModal.col).label, corregirModal.destino.beneficiario],
              ['Banco', corregirModal.destino.banco], ['CLABE', corregirModal.destino.clabe],
              ['Cuenta', corregirModal.destino.cuenta],
            ]} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>Monto *</label>
                <input type="number" value={corregirModal.campos.monto} onChange={e => setCorregirModal(m => ({ ...m, campos: { ...m.campos, monto: e.target.value } }))} style={{ ...css.input(false) }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>Sucursal</label>
                <select value={corregirModal.campos.sucursal} onChange={e => cambiarSucursalCorregir(e.target.value)} style={{ ...css.select(false) }}>
                  <option value="">— Selecciona sucursal —</option>
                  {(corregirModal.sucursalesOpc || []).map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>Centro de costos</label>
                <select value={corregirModal.campos.centroCostos} onChange={e => setCorregirModal(m => ({ ...m, campos: { ...m.campos, centroCostos: e.target.value } }))} style={{ ...css.select(false) }}>
                  <option value="">— Selecciona centro —</option>
                  {(corregirModal.centrosOpc || []).map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>Dirección</label>
                <select value={corregirModal.campos.direccion} onChange={e => setCorregirModal(m => ({ ...m, campos: { ...m.campos, direccion: e.target.value } }))} style={{ ...css.select(false) }}>
                  <option value="">— Selecciona dirección —</option>
                  {DIRECCIONES_FIJAS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>Folio CFDI</label>
                <input value={corregirModal.campos.folioCfdi} onChange={e => setCorregirModal(m => ({ ...m, campos: { ...m.campos, folioCfdi: e.target.value } }))} style={{ ...css.input(false) }} />
              </div>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>Motivo de pago</label>
              {corregirModal.motivoFijo ? (
                /* Tipos por concepto: el motivo de cada renglón es el que classifica
                   el gasto, y aquí sólo vive un resumen. Editarlo no cambiaría nada. */
                <>
                  <input value={corregirModal.campos.motivoPago} readOnly
                    style={{ ...css.input(false), background: 'var(--hair-soft)', color: 'var(--glass)' }} />
                  <div style={{ fontSize: 11.5, color: 'var(--glass)', marginTop: 4 }}>
                    Este tipo lleva un motivo por concepto: para cambiarlos hay que capturar una solicitud nueva.
                  </div>
                </>
              ) : corregirModal.campos.motivoPago ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ flex: 1, padding: '9px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600, background: 'rgba(22,163,74,0.10)', color: 'var(--ok)', border: '1.5px solid rgba(22,163,74,0.28)' }}>
                    💳 {corregirModal.campos.motivoPago}
                  </span>
                  <button onClick={() => { setCorregirModal(m => ({ ...m, campos: { ...m.campos, motivoPago: '' } })); setMotivoQ(''); }}
                    title="Cambiar motivo"
                    style={{ padding: '7px 11px', borderRadius: 8, border: '1px solid var(--hair)', background: 'var(--panel)', color: 'var(--glass)', cursor: 'pointer', fontSize: 13 }}>×</button>
                </div>
              ) : (
                <div>
                  <input value={motivoQ} onChange={e => setMotivoQ(e.target.value)} autoFocus
                    placeholder="🔍 Buscar motivo en el catálogo..." style={{ ...css.input(false) }} />
                  <div style={{ marginTop: 4, border: '1px solid var(--hair)', borderRadius: 10, maxHeight: 140, overflowY: 'auto', background: 'var(--panel)' }}>
                    {(() => {
                      const lista = corregirModal.motivosOpc || [];
                      const t = motivoQ.trim().toLowerCase();
                      const filtrados = (t ? lista.filter(m => m.toLowerCase().includes(t)) : lista).slice(0, 30);
                      if (!lista.length) return <div style={{ padding: '8px 12px', fontSize: 12, color: 'var(--glass)' }}>Este documento no tiene catálogo de motivos disponible.</div>;
                      if (!filtrados.length) return <div style={{ padding: '8px 12px', fontSize: 12, color: 'var(--glass)' }}>Sin coincidencias.</div>;
                      return filtrados.map(m => (
                        <div key={m} onClick={() => setCorregirModal(mm => ({ ...mm, campos: { ...mm.campos, motivoPago: m } }))}
                          style={{ padding: '6px 12px', fontSize: 12.5, color: 'var(--ink)', cursor: 'pointer', borderBottom: '1px solid var(--hair-soft)' }}
                          onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,84,28,0.04)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          {m}
                        </div>
                      ));
                    })()}
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--glass)', marginTop: 4 }}>
                    Al cambiar el motivo, la cuenta contable se vuelve a resolver con el catálogo.
                  </div>
                </div>
              )}
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>Concepto del pago</label>
              <textarea value={corregirModal.campos.observaciones} onChange={e => setCorregirModal(m => ({ ...m, campos: { ...m.campos, observaciones: e.target.value } }))} rows={2} style={{ ...css.input(false), resize: 'vertical' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>Nota del reenvío</label>
              <input value={corregirModal.nota} onChange={e => setCorregirModal(m => ({ ...m, nota: e.target.value }))} placeholder="Ej: Se corrigió el motivo y el monto" style={{ ...css.input(false) }} />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setCorregirModal(null)} style={{ ...css.btn('#747878', true), fontSize: 13 }}>Cancelar</button>
              <button onClick={confirmarReenvio} disabled={corregirModal.enviando} style={{ ...css.btn('#5e72e4'), fontSize: 13, opacity: corregirModal.enviando ? 0.6 : 1 }}>
                {corregirModal.enviando ? 'Reenviando…' : '🔁 Reenviar solicitud'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal cuenta contable */}
      <Modal open={!!cuentaModal?.open} onClose={() => setCuentaModal(null)} title="🧮 Cuenta contable del gasto" width={480}>
        {cuentaModal && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 13 }}>
            <div style={{ fontSize: 12.5, color: 'var(--glass)' }}>
              El motivo <b style={{ color: 'var(--ink)' }}>{cuentaModal.motivo}</b> existe en varias cuentas del plan contable.
              Elige la que clasifica este gasto ({cuentaModal.folio}){cuentaModal.modo === 'aprobar' ? ' para aprobarlo' : ''}.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {(cuentaModal.cuentas || []).map(c => (
                <label key={c} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 8, cursor: 'pointer',
                  border: `1px solid ${cuentaModal.seleccion === c ? 'var(--plasma)' : 'var(--hair)'}`,
                  background: cuentaModal.seleccion === c ? 'rgba(255,84,28,0.08)' : 'var(--panel)',
                }}>
                  <input type="radio" name="cuentaContable" checked={cuentaModal.seleccion === c}
                    onChange={() => setCuentaModal(m => ({ ...m, seleccion: c }))} style={{ accentColor: 'var(--plasma)' }} />
                  <span style={{ fontFamily: 'Geist Mono, monospace', fontSize: 13, color: 'var(--ink)' }}>{c}</span>
                  <span style={{ marginLeft: 'auto', fontSize: 10.5, color: 'var(--glass)' }}>
                    {c.startsWith('6000') ? 'Operación / Distribución' : c.startsWith('6100') ? 'Venta' : c.startsWith('6200') ? 'Administración' : c.startsWith('6300') ? 'Depreciación' : c.startsWith('6400') ? 'Amortización' : ''}
                  </span>
                </label>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setCuentaModal(null)} style={{ ...css.btn('#6b7280', true), fontSize: 13 }}>Cancelar</button>
              <button onClick={confirmarCuenta} style={{ ...css.btn('#5e72e4'), fontSize: 13 }}>
                {cuentaModal.modo === 'aprobar' ? '✓ Aprobar con esta cuenta' : 'Guardar cuenta'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de detalle */}
      <Modal open={!!detalle} onClose={() => setDetalle(null)} title={detalle ? `${detalle.folio || '—'} · ${TIPO_META[tipoDeItem(detalle)]?.label || 'Detalle'}` : ''} width={1040} zIndex={900}>
        {detalle && (
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div style={{ flex: '1 1 380px', minWidth: 320, display: 'flex', flexDirection: 'column', gap: 16, fontSize: 13 }}>
            {loadingDetalle && <div style={{ textAlign: 'center', padding: 20 }}><Spinner /></div>}

            {/* Alerta presupuesto */}
            {rebasados[detalle.centroCostos] && (
              <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.4)', color: 'var(--danger)', fontSize: 12.5, fontWeight: 700 }}>
                🚨 Presupuesto del centro "{detalle.centroCostos}" rebasado este mes
              </div>
            )}

            {/* Datos principales */}
            <div style={{ display: 'grid', gap: 0 }}>
              {[
                ['Compañía', detalle.empresa],
                ['Proveedor / Beneficiario', detalle.proveedor || detalle.comprobador],
                ['Banco', detalle.cuentaPorConfirmar ? <AvisoCuentaProveedor s={detalle} /> : detalle.banco],
                ['CLABE', detalle.cuentaPorConfirmar ? (['admin', 'finanzas', 'programacion'].includes(user?.rol) && !['pagada', 'rechazada'].includes(detalle.estado)
                  ? <button onClick={() => setConfirmaCuenta({ sol: detalle, estado: null })} style={{ ...css.btn('#00b8c4'), fontSize: 12, padding: '4px 10px' }}>Elegir cuenta</button>
                  : 'Por confirmar') : detalle.clabe && (
                  <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>{detalle.clabe}<AvisoCuentaProveedor s={detalle} /></span>
                )],
                ['Cuenta confirmada por', detalle.cuentaConfirmada?.por ? `${detalle.cuentaConfirmada.por} · ${new Date(detalle.cuentaConfirmada.fecha).toLocaleDateString('es-MX')}` : null],
                ['Sucursal', detalle.sucursal],
                ['Centro de Costos', detalle.centroCostos],
                ['Dirección', detalle.direccion],
                ['Monto (base)', detalle.monto != null ? `$${fmt(detalle.monto)} ${detalle.moneda || 'MXN'}` : null],
                ['Total c/ impuestos', (detalle.montoNeto != null && detalle.montoNeto !== detalle.monto)
                  ? `$${fmt(detalle.montoNeto)} ${detalle.moneda || 'MXN'}` : null],
                ['Forma de Pago', detalle.formaPago],
                ['CFDI', detalle.folioCfdi],
                ['Motivo', detalle.motivoPago || detalle.conceptoGasto],
                ['Concepto del Pago', detalle.observaciones],
                ['Estado', ESTADO_LABEL[detalle.estado] || detalle.estado], ['Pagada desde', detalle.cuentaPago ? [detalle.cuentaPago.banco, detalle.cuentaPago.terminacion && `••${detalle.cuentaPago.terminacion}`].filter(Boolean).join(' ') : null],
                ['Solicitante', detalle.usuario?.nombre || detalle.solicitante],
                // Cuenta contable: solo para solicitudes y si el rol permite verla o ya está asignada
                detalle._coleccion === 'solicitud' ? ['Cuenta contable', (detalle.cuentaContable || ['contador', 'admin'].includes(user?.rol)) ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontFamily: 'Geist Mono, monospace', color: detalle.cuentaContable ? '#45dfa4' : '#ffb4ab' }}>
                      {detalle.cuentaContable || 'sin clasificar'}
                    </span>
                    {['contador', 'admin'].includes(user?.rol) && (
                      <button onClick={abrirEditarCuenta} title="Cambiar cuenta contable"
                        style={{ padding: '1px 8px', borderRadius: 6, border: '1px solid var(--hair)', background: 'var(--hair-soft)', color: 'var(--glass)', cursor: 'pointer', fontSize: 11 }}>✏️</button>
                    )}
                  </span>
                ) : null] : null,
              ].filter(row => row && row[1]).map(([l, v]) => (
                <div key={l} style={{ display: 'flex', gap: 10, padding: '6px 0', borderBottom: '1px solid var(--hair-soft)' }}>
                  <span style={{ fontSize: 11, color: 'var(--glass)', width: 140, flexShrink: 0, textTransform: 'uppercase', letterSpacing: '0.04em', paddingTop: 1 }}>{l}</span>
                  <span style={{ color: 'var(--ink)', fontWeight: 500 }}>{v}</span>
                </div>
              ))}
              {detalle.motivoRechazo && (
                <div style={{ display: 'flex', gap: 10, padding: '8px', background: 'rgba(255,45,85,0.08)', borderRadius: 6, border: '1px solid rgba(255,45,85,0.2)', marginTop: 4 }}>
                  <span style={{ fontSize: 11, color: 'var(--danger)', width: 140, flexShrink: 0, textTransform: 'uppercase', fontWeight: 700 }}>Motivo rechazo</span>
                  <span style={{ color: 'var(--danger)', fontWeight: 500 }}>{detalle.motivoRechazo}</span>
                </div>
              )}
            </div>

            {/* Archivos adjuntos */}
            {(detalle.comprobantes?.length > 0 || puedeSubirComprobante) && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Archivos adjuntos{detalle.comprobantes?.length ? ` (${detalle.comprobantes.length})` : ''}
                  </div>
                  {puedeSubirComprobante && (
                    <>
                      <input ref={compInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.xml" multiple style={{ display: 'none' }}
                        onChange={e => subirComprobante(e.target.files)} />
                      <button onClick={() => compInputRef.current?.click()} disabled={subiendoComp}
                        title="Sube el comprobante de pago; quedará en el expediente"
                        style={{ ...css.btn('#5e72e4'), fontSize: 12, padding: '6px 12px', opacity: subiendoComp ? 0.6 : 1 }}>
                        {subiendoComp ? 'Subiendo…' : '⬆️ Subir comprobante de pago'}
                      </button>
                    </>
                  )}
                </div>
                {detalle.comprobantes?.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 200, overflowY: 'auto' }}>
                    {detalle.comprobantes.map(a => (
                      <div key={a._id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 12px', borderRadius: 8, background: 'var(--panel)', border: '1px solid var(--hair)' }}>
                        <span style={{ fontSize: 15, flexShrink: 0 }}>{a.tipo === 'xml' ? '📋' : a.tipo === 'pdf' ? '📄' : '🖼️'}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12.5, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.nombreOriginal}</div>
                          {a.concepto && <div style={{ fontSize: 10.5, color: 'var(--glass)' }}>{a.concepto}</div>}
                        </div>
                        {['pdf', 'ticket'].includes(a.tipo) && (
                          <button onClick={() => verAdjunto(a._id)} title="Visualizar en el navegador"
                            style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid var(--hair)', background: 'var(--hair-soft)', color: 'var(--ink-soft)', cursor: 'pointer', fontSize: 12, flexShrink: 0 }}>👁 Ver</button>
                        )}
                        <button onClick={() => descargarAdjunto(a._id, a.nombreOriginal)} title="Descargar"
                          style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid var(--hair)', background: 'var(--hair-soft)', color: 'var(--ink-soft)', cursor: 'pointer', fontSize: 12, flexShrink: 0 }}>⬇</button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: 'var(--glass)' }}>Aún no hay archivos adjuntos. Sube aquí el comprobante de pago.</div>
                )}
              </div>
            )}

            {/* Historial de estados */}
            {detalle.historialEstados?.length > 0 && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Historial de estados</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {detalle.historialEstados.map((h, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12 }}>
                      <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 700, background: `${ESTADO_COLORS[h.estado]}18`, color: ESTADO_COLORS[h.estado], border: `1px solid ${ESTADO_COLORS[h.estado]}40`, whiteSpace: 'nowrap' }}>{ESTADO_LABEL[h.estado] || h.estado}</span>
                      <span style={{ color: 'var(--glass)' }}>{h.nombreUsuario}</span>
                      {h.motivo && <span style={{ color: 'var(--glass)', fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>"{h.motivo}"</span>}
                      <span style={{ color: 'var(--glass)', marginLeft: 'auto', whiteSpace: 'nowrap', fontSize: 11 }}>{h.fecha ? new Date(h.fecha).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Comentarios */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                Comentarios {detalle.comentarios?.length > 0 && `(${detalle.comentarios.length})`}
              </div>
              {detalle.comentarios?.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10, maxHeight: 180, overflowY: 'auto' }}>
                  {detalle.comentarios.map((c, i) => (
                    <div key={i} style={{ background: 'var(--hair-soft)', borderRadius: 8, padding: '8px 12px', border: '1px solid var(--hair)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink)' }}>{c.nombre}</span>
                          {c.rol && <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: `${ROL_COLORS[c.rol] || '#5b6068'}18`, color: ROL_COLORS[c.rol] || '#5b6068', fontWeight: 600, border: `1px solid ${ROL_COLORS[c.rol] || '#5b6068'}25` }}>{c.rol}</span>}
                        </div>
                        <span style={{ fontSize: 11, color: 'var(--glass)' }}>{c.fecha ? new Date(c.fecha).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}</span>
                      </div>
                      <div style={{ fontSize: 13, color: 'var(--ink-soft)' }}>{c.texto}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: 12, color: 'var(--glass)', marginBottom: 8 }}>Sin comentarios aún.</div>
              )}
              {user?.rol !== 'director' && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <input ref={comentarioRef} value={comentario} onChange={e => setComentario(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviarComentario(); } }}
                    placeholder="Escribe un comentario... (Enter para enviar)"
                    style={{ ...css.input(false), flex: 1, fontSize: 12.5 }} />
                  <button onClick={enviarComentario} disabled={!comentario.trim() || enviandoComent}
                    style={{ ...css.btn('#0369a1'), fontSize: 12, padding: '0 16px', flexShrink: 0 }}>
                    {enviandoComent ? '...' : 'Enviar'}
                  </button>
                </div>
              )}
            </div>

            <button onClick={() => handlePDF(detalle)} style={{ ...css.btn('#5e72e4') }}>⬇️ Descargar PDF</button>
          </div>

          <div style={{ flex: '1 1 420px', minWidth: 320 }}>
            <VisorAdjuntos endpoint={epBase(detalle)} id={detalle._id} comprobantes={detalle.comprobantes || []} folio={detalle.folio} />
          </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
